import React, { useState, useEffect } from 'react';
import {
    Skeleton,
    Box,
    Typography,
    LinearProgress,
    Fade,
    useTheme,
    SxProps,
    Theme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner,
    faCalculator,
    faGlobe,
    faFileInvoice,
    faPaperPlane,
    faCommentDots,
    faDatabase,
    faCloud,
} from '@fortawesome/sharp-solid-svg-icons';

interface EnhancedPanelBoneProps {
    /** Mode d'affichage */
    mode?: 'simple' | 'enhanced';
    /** Durée minimale d'affichage */
    minDuration?: number;
    /** Largeur */
    width?: string;
    /** Hauteur */
    height?: string;
    /** Styles personnalisés */
    sx?: SxProps<Theme>;
}

const loadingMessages = [
    { message: 'Génération de votre aperçu en cours...', icon: faCommentDots },
    { message: 'Mise en forme des données...', icon: faFileInvoice },
    { message: 'Traitement des informations...', icon: faCalculator },
    { message: 'Actualisation des données...', icon: faDatabase },
    { message: "Optimisation de l'affichage...", icon: faPaperPlane },
    { message: 'Préparation du document...', icon: faFileInvoice },
    { message: 'Finalisation des éléments...', icon: faGlobe },
    { message: 'Chargement des composants...', icon: faCloud },
    { message: 'Agencement des informations...', icon: faFileInvoice },
    { message: 'Vérification des données...', icon: faSpinner },
];

export const EnhancedPanelBone: React.FunctionComponent<EnhancedPanelBoneProps> = function ({
    mode = 'simple',
    minDuration = 500,
    width = '90%',
    height = '100%',
    sx = {},
}) {
    const theme = useTheme();
    const [progress, setProgress] = useState(0);
    const [messageIndex, setMessageIndex] = useState(0);
    const [showMessage, setShowMessage] = useState(true);

    useEffect(() => {
        const duration = Math.max(minDuration, 5000);
        const interval = setInterval(() => {
            setProgress((prev) => Math.min(prev + 100 / (duration / 200), 99));
        }, 200);
        return () => clearInterval(interval);
    }, [minDuration]);

    useEffect(() => {
        if (mode !== 'enhanced') return undefined;

        const interval = setInterval(() => {
            setShowMessage(false);
            setTimeout(() => {
                setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
                setShowMessage(true);
            }, 500);
        }, 3000);

        return () => clearInterval(interval);
    }, [mode]);

    if (mode === 'simple') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    ...sx,
                }}
            >
                <Skeleton
                    variant="rounded"
                    sx={{ width, height }}
                />
            </Box>
        );
    }

    const currentMessage = loadingMessages[messageIndex];
    const opacity = '0.8';
    const iconGrey =
        theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[400];
    const textGrey =
        theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.grey[200];
    const progressColor =
        theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[500];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                position: 'relative',
                ...sx,
            }}
        >
            <Skeleton
                variant="rounded"
                sx={{
                    position: 'absolute',
                    zIndex: 1,
                    width,
                    height,
                }}
            />

            <Box
                sx={{
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '80%',
                    height: '50%',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        fontSize: '3rem',
                        color: iconGrey,
                        marginBottom: 2,
                        filter: `opacity(${opacity})`,
                    }}
                >
                    <FontAwesomeIcon
                        icon={currentMessage.icon}
                        spin={currentMessage.icon === faSpinner}
                    />
                </Box>

                <Fade
                    in={showMessage}
                    timeout={500}
                >
                    <Typography
                        variant="h5"
                        align="center"
                        sx={{
                            marginBottom: 2,
                            color: textGrey,
                            fontWeight: 600,
                            filter: `opacity(${opacity})`,
                        }}
                    >
                        {currentMessage.message}
                    </Typography>
                </Fade>

                <Box sx={{ width: '70%', marginTop: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            filter: `opacity(${opacity})`,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: progressColor,
                            },
                        }}
                    />
                    <Typography
                        variant="body1"
                        align="right"
                        sx={{
                            marginTop: 0.5,
                            color: textGrey,
                            fontWeight: 500,
                            filter: `opacity(${opacity})`,
                        }}
                    >
                        {`${Math.round(progress)}%`}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
